import { hot } from 'react-hot-loader/root'
import React, { useEffect } from 'react'
import { Router, Location } from '@reach/router'
import { ModalShell, ErrorBoundary } from 'components/common'
import { isModalView, hasFeature, serverData } from 'utils/server-data'
import {
  isBdmcConsolidationEnabled,
  isBdmcGatedClientRequest,
  shouldUseBdmcSummaryOrRewardsHome,
} from '@/screens/bdmc/shared/consolidation'
import { setNewFlowModal, isGBDebitCardEnrollmentFlow } from 'utils/flow'
import { CLIENT_ROUTE_PATHS } from 'utils/routes'
import { Global } from '@emotion/core'
import globalStyles from './global-styles'
import {
  // v1 Flows
  EnrollVirtualCard,
  CancelCard,
  CancelCardProduct,
  ChangePin,
  Summary,
  ViewPin,
  Funding,
  FundingProduct,
  ChangeCvv,
  NotFound,
  ProductEligibilityResult,

  // v2 Flows
  EnrollCardFlow,
  ActivateCardFlow,
  ChangeAddressFlow,
  ConfirmCardAddressFlow,
  AddAddressFlow,
  ReplaceCardFlow,
  SetPinFlow,
  StaticPageFlow,
  ShowCardFlow,
  LowBalanceAlertSettingsFlow,
  RequestPhysicalCardFlow,
  XoPpdcFlow,
  SetupFI,

  // Main Page Template
  ManageCard,
  BdmcSummary,
  BdmcRewards,
  BdmcEnrollFlow,
  BdmcRecurringPayments,
  BdmcFaqPage,
  BdmcOptOutPage,
  PpccLandingPage,
  Rewards,
  CardLimits,
  LostCardPostStepupPage,
} from './app-flows'

const {
  DECOMMISSIONED_ENROLLMENT,
  ENROLL_CARD,
  BDMC_ENROLL,
  BDMC_ENROLL_ADDITIONAL,
  PRODUCT_ELIGIBILITY,
  SEARCH,
  SUMMARY,
  REWARDS,
  XO_PPDC,
  FUNDING,
  FUNDING_PRODUCT,
  CANCEL_CARD_BY_PRODUCT,
  LOW_BALANCE_ALERT_SETTINGS,
  CHANGE_PIN,
  VIEW_PIN,
  CHANGE_CVV,
  CANCEL_CARD,
  CHANGE_ADDRESS,
  BDMC_CONFIRM_CARD_ADDRESS,
  ADD_ADDRESS,
  REPLACE_CARD,
  ACTIVATE_CARD,
  ACTIVATE_CARD_BY_ID,
  SET_PIN,
  STATIC_PAGE,
  PRODUCT_STATIC_PAGE,
  SHOW_CARD,
  REQUEST_PHYSICAL_CARD,
  SETUP_FUNDING,
  BDMC_RECURRING_PAYMENTS,
  BDMC_FAQ,
  BDMC_OPTOUT,
  PPCC_LANDING_PAGE,
  CARD_LIMITS,
  BDMC_CANCEL_CARD,
  BDMC_REPLACE_CARD,
  BDMC_LOST_CARD_POST_STEPUP,
} = CLIENT_ROUTE_PATHS

// Cast connected components to any to allow @reach/router's path prop
const EnrollVirtualCardRoute = EnrollVirtualCard as any
const SummaryRoute = Summary as any
const ShowCardFlowRoute = ShowCardFlow as any
const CancelCardRoute = CancelCard as any
const ChangePinRoute = ChangePin as any
const ViewPinRoute = ViewPin as any
const FundingRoute = Funding as any
const ProductEligibilityResultRoute = ProductEligibilityResult as any
const CancelCardProductRoute = CancelCardProduct as any
const ChangeCvvRoute = ChangeCvv as any
const FundingProductRoute = FundingProduct as any
const EnrollCardFlowRoute = EnrollCardFlow as any
const ActivateCardFlowRoute = ActivateCardFlow as any
const ChangeAddressFlowRoute = ChangeAddressFlow as any
const ConfirmCardAddressFlowRoute = ConfirmCardAddressFlow as any
const AddAddressFlowRoute = AddAddressFlow as any
const ReplaceCardFlowRoute = ReplaceCardFlow as any
const SetPinFlowRoute = SetPinFlow as any
const LowBalanceAlertSettingsFlowRoute = LowBalanceAlertSettingsFlow as any
const StaticPageFlowRoute = StaticPageFlow as any
const RequestPhysicalCardFlowRoute = RequestPhysicalCardFlow as any
const SetupFIRoute = SetupFI as any
const XoPpdcFlowRoute = XoPpdcFlow as any
const ManageCardRoute = ManageCard as any
const BdmcSummaryRoute = BdmcSummary as any
const BdmcRewardsRoute = BdmcRewards as any
const BdmcEnrollFlowRoute = BdmcEnrollFlow as any
const BdmcRecurringPaymentsRoute = BdmcRecurringPayments as any
const BdmcFaqPageRoute = BdmcFaqPage as any
const BdmcOptOutPageRoute = BdmcOptOutPage as any
const PpccLandingPageRoute = PpccLandingPage as any
const RewardsRoute = Rewards as any
const CardLimitsRoute = CardLimits as any
const LostCardPostStepupPageRoute = LostCardPostStepupPage as any
const NotFoundRoute = NotFound as any

function isBdmcUser(): boolean {
  return serverData.isBdmcExperience === true
}

function isBdmcExperienceEnabled(): boolean {
  return isBdmcUser() && isBdmcConsolidationEnabled()
}

/**
 * When consolidation is OFF, newly ported BDMC-only UCS routes show Page not found
 * (msmaster / confirm-card-address parity). Shared pre-consolidation /manage/* flows
 * stay available for BDMC and PPDC.
 */
function shouldRenderBdmcConsolidationNotFound(pathname: string): boolean {
  if (isBdmcConsolidationEnabled()) {
    return false
  }

  // Explicit CONSUMER / VIRTUAL productName must not be treated as BDMC (isBdmcExperience
  // is set for all business accounts via addBdmcLocals).
  if (!isBdmcGatedClientRequest()) {
    return false
  }

  const path = String(pathname || '')
  return (
    path.includes('/confirm-card-address') ||
    path.includes('/business/enroll') ||
    path.includes('/card-limits/') ||
    path.includes('/recurring-payments-dashboard') ||
    path.includes('/faq') ||
    path.includes('/optout') ||
    /\/(cancel-card|replace-card|report-lost|lost-card-post-stepup)(\/|$)/.test(path)
  )
}

function renderAppRoutes() {
  const bdmcExperienceEnabled = isBdmcExperienceEnabled()
  const bdmcSummaryOrRewardsHome = shouldUseBdmcSummaryOrRewardsHome()

  return (
    <Router>
      {/* v1 Flows */}
      <EnrollVirtualCardRoute path={DECOMMISSIONED_ENROLLMENT} />
      <SummaryRoute path={SEARCH} />
      <ShowCardFlowRoute path={SHOW_CARD} />
      <CancelCardRoute path={CANCEL_CARD} />
      <ChangePinRoute path={CHANGE_PIN} />
      <ViewPinRoute path={VIEW_PIN} />

      {/* TODO Can the funding URL can be dynamic based on type? */}
      <FundingRoute path={FUNDING} />
      <ProductEligibilityResultRoute path={PRODUCT_ELIGIBILITY} />
      <PpccLandingPageRoute path={PPCC_LANDING_PAGE} />

      {/* TODO Decommissioned - To review and remove later */}
      {/* Temporary for Virtual Card until one card management page unifies physical and virtual */}
      <CancelCardProductRoute path={CANCEL_CARD_BY_PRODUCT} />
      <ChangeCvvRoute path={CHANGE_CVV} />
      <FundingProductRoute path={FUNDING_PRODUCT} />

      {/* v2 Flows */}
      <EnrollCardFlowRoute path={ENROLL_CARD} />
      {bdmcExperienceEnabled && <BdmcEnrollFlowRoute path={BDMC_ENROLL} />}
      {bdmcExperienceEnabled && <BdmcEnrollFlowRoute path={BDMC_ENROLL_ADDITIONAL} />}
      <ActivateCardFlowRoute path={ACTIVATE_CARD} />
      <ActivateCardFlowRoute path={ACTIVATE_CARD_BY_ID} />
      {/* Shared confirm-card-address: PPDC / explicit consumer always; BDMC only when consolidation ON. */}
      {(!isBdmcGatedClientRequest() || isBdmcConsolidationEnabled()) && (
        <ConfirmCardAddressFlowRoute path={BDMC_CONFIRM_CARD_ADDRESS} />
      )}
      <ChangeAddressFlowRoute path={CHANGE_ADDRESS} />
      <AddAddressFlowRoute path={ADD_ADDRESS} />
      <ReplaceCardFlowRoute path={REPLACE_CARD} />
      {bdmcExperienceEnabled && <ReplaceCardFlowRoute path={BDMC_REPLACE_CARD} />}
      {bdmcExperienceEnabled && <LostCardPostStepupPageRoute path={BDMC_LOST_CARD_POST_STEPUP} />}
      {bdmcExperienceEnabled && <CancelCardRoute path={BDMC_CANCEL_CARD} />}
      <SetPinFlowRoute path={SET_PIN} />
      <LowBalanceAlertSettingsFlowRoute path={LOW_BALANCE_ALERT_SETTINGS} />
      <StaticPageFlowRoute path={PRODUCT_STATIC_PAGE} />
      <StaticPageFlowRoute path={STATIC_PAGE} />
      <RequestPhysicalCardFlowRoute path={REQUEST_PHYSICAL_CARD} />
      <SetupFIRoute path={SETUP_FUNDING} />
      {bdmcExperienceEnabled && <CardLimitsRoute path={CARD_LIMITS} />}

      {/* XO-PPDC Flow */}
      <XoPpdcFlowRoute path={XO_PPDC} />
      {/* Main page template — productName=CONSUMER always mounts PPDC home */}
      {bdmcSummaryOrRewardsHome ? (
        <BdmcSummaryRoute path={SUMMARY} />
      ) : (
        <ManageCardRoute path={SUMMARY} />
      )}
      {bdmcSummaryOrRewardsHome ? (
        <BdmcRewardsRoute path={REWARDS} />
      ) : (
        hasFeature('enableRewardsExperience') && <RewardsRoute path={REWARDS} />
      )}
      {bdmcExperienceEnabled && <BdmcRecurringPaymentsRoute path={BDMC_RECURRING_PAYMENTS} />}
      {bdmcExperienceEnabled && <BdmcFaqPageRoute path={BDMC_FAQ} />}
      {bdmcExperienceEnabled && <BdmcOptOutPageRoute path={BDMC_OPTOUT} />}
      <NotFoundRoute default />
    </Router>
  )
}

function App() {
  // Set body attribute for GB enrollment flows to enable conditional styling
  useEffect(() => {
    const isGBEnrollment = isGBDebitCardEnrollmentFlow()

    if (isGBEnrollment) {
      document.body.setAttribute('data-gb-enrollment', 'true')
    } else {
      document.body.removeAttribute('data-gb-enrollment')
    }

    if (document.body.hasAttribute('data-pin-create-flow') === true) {
      document.body.removeAttribute('data-pin-create-flow')
    }

    // Cleanup on unmount
    return () => {
      document.body.removeAttribute('data-gb-enrollment')
    }
  }, [])

  return (
    <ErrorBoundary>
      <Global styles={globalStyles} />
      <Location>
        {({ location }) => {
          if (isModalView()) {
            setNewFlowModal(`${location.pathname}${location.search}`)
          }

          const content = shouldRenderBdmcConsolidationNotFound(location.pathname) ? (
            <NotFoundRoute default />
          ) : (
            renderAppRoutes()
          )

          return <React.Suspense fallback={<ModalShell />}>{content}</React.Suspense>
        }}
      </Location>
    </ErrorBoundary>
  )
}

export default hot(App)
